import React, { useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';


import { Bold, MenuItem, Select } from '../../../../Styles';
import { FurtherInstructions, InputGroup, SectionRow, TopInstructions } from './styles';

import { downloadTemplateFile, setUploadInventoryFileAction, setUploadInventoryFileBatch, uploadFilledTemplateFile } from '../../../../../actions/inventoryActionsV2';
import { getRestrictedFields } from '../../../../../commons/utils';
import { purgeState } from '../../../../../actions/commonActions';

import { BulkUploadDialog } from '@dentira/dentira-bulk-upload-widget';
import { Close, CloudDownload, CloudUpload } from '@material-ui/icons';
import { Box, Button, Dialog, DialogContent, DialogTitle, Divider, IconButton, Typography } from '@mui/material';
import { API_KEY, HOST } from '../../../../../commons/backend/api-config';
import { uuid } from 'uuidv4';

const BulkUpdateSKUPrice = ({ open, onSuccess, onClose, history, ...props }) => {
	const [selectedBucket, setSelectedBucket] = useState('Base Price');
	const [bulkOpen,setBulkOpen] = useState(false);
	const [uploadOpen,setUploadOpen] = useState(false);
	const downloadTemplate = async (action, onError) => {
		const { downloadTemplateFile } = props;

	let body = {
			bucket: selectedBucket === 'Base Price' ? 'price' : selectedBucket,
		};

		await downloadTemplateFile(action, onError, body);
	};

	const uploadFilledTemplate = async (action, formData, onSuccess, onError) => {
		const { uploadFilledTemplateFile } = props;

		await uploadFilledTemplateFile(action, formData, onSuccess, onError);
	};

	let { buckets, uploadInventoryFileAction, uploadInventoryFileBatch, uploadInventoryFileType, session, supplier } = props;
	let { setUploadInventoryFileAction, setUploadInventoryFileBatch, purgeState: purgeStateAction } = props;
	
	const handleCloseBulkUploadDialog = () => {
		setBulkOpen(false);
		setUploadOpen(false);
	};

	const checkSessionAndProceed = (callback) => {
		// Check if session is expired or invalid
		if (!session || !session.session_token || session.login === true) {
			// Session is expired or invalid, logout and redirect
			purgeStateAction();
			if (history) {
				history.push('/');
			} else {
				window.location.href = '/';
			}
			return false;
		}

		// Session is valid, proceed with callback
		callback();
		return true;
	};

	const handleOpenBulk = () => {
		checkSessionAndProceed(() => {
			setBulkOpen(true);
		});
	}
	
	const handleOpenBulkUpload = () => {
		checkSessionAndProceed(() => {
			setUploadOpen(true);
			handleOpenBulk();
		});
	}

	const config = useMemo(
		() => (!uploadOpen?{
		  title: "Download Template",
		  type: 'BULK_PRICE_UPDATE_LABS_SELLER',
		  isOpen: bulkOpen,
		  handleClose: handleCloseBulkUploadDialog,
		  fileType: 'EXCEL',
		  rules: [],
		  columns: [],
		  session: session?.session_token || session,
		  region: HOST.toLowerCase(),
		  apiKey: API_KEY,
		  downloadTemplateConfig: {
			hasDynamicTemplate: true,
			showDownloadButton: true,
			downloadButtonLabel: 'Download Cases',
			downloadRequestPayload: {
				supplier: supplier,
				bucketId: selectedBucket === 'Base Price' ?  'base' : selectedBucket,
				request_id: uuid()
			},
			autoInitializeDownload: true,
		  },
		  uploadConfig: {
			showUploadButton: false,
		  },
		  onUploadSuccess: handleCloseBulkUploadDialog,
		}:{
			title: 'Upload Template',
			type: 'BULK_PRICE_UPDATE_LABS_SELLER',
			isOpen: open,
			region: HOST.toLowerCase(),
			handleClose: handleCloseBulkUploadDialog,
			fileType: 'EXCEL',
			rules: [],
			columns: ['Lab SKU', 'Lab SKU Name', 'Base Price', 'Bucket Name', 'Bucket Price'],
			session: session?.session_token || session,
			apiKey: API_KEY,
			downloadTemplateConfig: {
			  showDownloadButton: false,
			},
			uploadConfig: {
			  showUploadButton: true,
			  uploadRequestPayload: {
				supplier: supplier,
				request_id: uuid()
			  },
			},
			onUploadSuccess: handleCloseBulkUploadDialog,
		  }),
		[bulkOpen, selectedBucket, session, uploadOpen]
	);

	const getFurtherInstructions = () => {
		if (selectedBucket === 'Base Price') {
			return (
				<FurtherInstructions>
					<Bold>Template columns:</Bold>
					<ol>
						<li>Lab SKU</li>
						<li>Lab SKU Name</li>
						<li>Base Price (editable)</li>
					</ol>
					<Bold>Notes:</Bold>
					<ul>
						<li>Do <Bold>not</Bold> alter the file headers or pre-filled values in <em>Lab SKU</em> or <em>Lab SKU Name</em>.</li>
						<li><em>Base Price</em> denotes the generic price that will be shown to clinics/DSOs where there's no contractual pricing agreement.</li>
						<li>If a SKU should not be considered for generic locations/DSOs, enter <Bold>"NA"</Bold> in the <em>Base Price</em> column.</li>
					</ul>
				</FurtherInstructions>
			);
		} else {
			return (
				<FurtherInstructions>
					<Bold>Template columns:</Bold>
					<ol>
						<li>Lab SKU</li>
						<li>Lab SKU Name</li>
						<li>Base Price</li>
						<li>Bucket Name</li>
						<li>Bucket Price (editable)</li>
					</ol>
					<Bold>Notes:</Bold>
					<ul>
						<li>Do <Bold>not</Bold> alter the file headers or pre-filled values in <em>Lab SKU</em>, <em>Lab SKU Name</em>, <em>Base Price</em>, or <em>Bucket Name</em>.</li>
						<li>If a SKU should not be included in the selected bucket, enter <Bold>"NA"</Bold> in the <em>Bucket Price</em> column.</li>
					</ul>
				</FurtherInstructions>
			);
		}
	};

	return (
		<Dialog fullScreen open={open} onClose={onClose}>
			<DialogTitle sx={{justifyContent:"space-between", display:"flex", alignItems:"center", pb: 1,pl: 10}}>
				<Box>
					<Typography variant="h5" sx={{ fontWeight: 600, color: '#333', mb: 0.5 }}>
						Bulk Update SKU Price
					</Typography>
					<Typography variant="body2" sx={{ color: '#666', fontWeight: 400 }}>
						Update pricing for multiple SKUs at once using a spreadsheet template
					</Typography>
				</Box>
				<IconButton onClick={onClose} ><Close /></IconButton>
			</DialogTitle>
			<Divider/>

			<DialogContent sx={{ px: 10, py: 5 }}>
				<TopInstructions>
					<p>Select the bucket from the "Select bucket" dropdown. If you want to update the base price, just select "Base Price" in the dropdown.</p>
					<p>Once done with the dropdown selection, please read the instructions and click on the Download Template button.</p>
					<p>After making changes in the sheet, click on Upload updated sheet button.</p>
					<p>The processing might take a few seconds. Once done, you will be able to see the SKUs updated count. If there was an error in the upload file, you will see the relevant error message.</p>
				</TopInstructions>

				<SectionRow>
					<InputGroup
						label='Select bucket'
						markRequired={true}
					>
						<Typography variant="body1" sx={{ fontWeight: 600, marginBottom: '0.5rem' }}>Select bucket</Typography>
						<Select
							name={'selected_bucket'}
							placeholder='Select Bucket'
							value={selectedBucket}
							disabled={false}
							onChange={(e) => {
								setSelectedBucket(e.target.value);
							}}
						>
							<MenuItem value="Base Price">Base Price</MenuItem>
							{buckets.map((bucket) => (
								<MenuItem
									key={bucket?.id}
									value={bucket?.id}
									disabled={bucket?.pending_map_updates > 0}
								>
									{bucket?.alias}
								</MenuItem>
							))}
						</Select>
					</InputGroup>
				</SectionRow>
				{getFurtherInstructions()}
				
				<Box sx={{ justifyContent: 'start', display: 'flex', gap: 2, mt: 3, mb: 2 }}>
					<Button 
						onClick={handleOpenBulk} 
						variant="outlined"
						size='small'
						startIcon={<CloudDownload />}
						sx={{
							borderColor: '#666',
							color: '#333',
							backgroundColor: '#fff',
							'&:hover': {
								borderColor: '#333',
								backgroundColor: '#f5f5f5',
							},
							textTransform: 'none',
							fontWeight: 500,
						}}
					>
						Download Template
					</Button>
					<Button 
						onClick={handleOpenBulkUpload} 
						variant="contained"
						size='small'
						startIcon={<CloudUpload />}
						sx={{
							backgroundColor: '#424242',
							color: '#fff',
							'&:hover': {
								backgroundColor: '#212121',
							},
							textTransform: 'none',
							fontWeight: 500,
						}}
					>
						Upload updated sheet
					</Button>
				</Box>
			</DialogContent>
			{bulkOpen && (
				<BulkUploadDialog 
					config={config} 
				/>
			)}

			</Dialog>
	);
};

const mapStateToProps = (state) => {
	let restrictedFields = getRestrictedFields(state?.session, state?.supplier);
	let buckets = state?.inventory?.buckets || [];
	buckets = buckets.slice().sort((a, b) => (a?.alias || '').toLowerCase().localeCompare((b?.alias || '').toLowerCase()));

	return {
		buckets,
		restrictedFields,
		supplier: state?.supplier?.supplier || '',
		session: state?.session,
		uploadInventoryFileAction: state?.inventory?.uploadInventoryFileAction || '',
		uploadInventoryFileBatch: state?.inventory?.uploadInventoryFileBatch || '',
		uploadInventoryFileType: state?.inventory?.uploadInventoryFileType || '',
	};
};

const mapDispatch = {
	downloadTemplateFile,
	uploadFilledTemplateFile,
	setUploadInventoryFileAction,
	setUploadInventoryFileBatch,
	purgeState,
};

export default withRouter(connect(mapStateToProps, mapDispatch)(BulkUpdateSKUPrice));
